<template>
  <div class="motd-overlay" @click.self="$emit('close')">
    <div class="motd-dialog">
      <div class="motd-header">
        <h3>Voicemail greeting</h3>
        <button type="button" class="motd-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="motd-body">
        <p class="motd-hint">
          Check one pre-recorded message and callers hear that recording.
          Leave them all unchecked and Twilio speaks the text below.
        </p>

        <div v-if="loading" class="motd-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>

        <template v-else>
          <label class="motd-field-label" for="voicemail-say">Text greeting</label>
          <textarea
            id="voicemail-say"
            v-model="say"
            rows="4"
            maxlength="1500"
            placeholder="You've reached..."
          ></textarea>
          <p class="motd-hint">{{ say.trim().length }} / 1500</p>
          <p v-if="errorText" class="motd-error">{{ errorText }}</p>
          <p v-if="successText" class="motd-success">{{ successText }}</p>

          <h4 class="prompt-heading">Pre-recorded messages</h4>
          <p v-if="!tableReady" class="motd-error">
            The message library is not set up yet.
          </p>
          <template v-else>
            <p class="motd-hint">
              {{
                selectedId
                  ? 'Callers hear the checked message.'
                  : 'Nothing is checked, so callers hear the text greeting.'
              }}
            </p>
            <div class="prompt-actions">
              <button
                v-if="!recording"
                type="button"
                class="motd-btn motd-btn-secondary"
                :disabled="savingPrompt"
                @click="startRecording"
              >
                <i class="fas fa-microphone"></i> Record
              </button>
              <button
                v-else
                type="button"
                class="motd-btn motd-btn-primary"
                @click="stopRecording"
              >
                <i class="fas fa-stop"></i> Stop {{ formatClock(recordElapsed) }}
              </button>
              <button
                type="button"
                class="motd-btn motd-btn-secondary"
                :disabled="recording || savingPrompt"
                @click="pickUpload"
              >
                <i class="fas fa-upload"></i> Upload
              </button>
              <input
                ref="fileInput"
                class="prompt-file"
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.webm,.ogg"
                @change="onUpload"
              />
            </div>

            <div v-if="draftUrl" class="prompt-draft">
              <div class="voice-scrub">
                <span class="voice-scrub-time">{{ formatClock(draftCurrent) }}</span>
                <input
                  class="voice-scrub-bar"
                  type="range"
                  min="0"
                  step="0.05"
                  :max="draftDuration || 0"
                  :value="draftCurrent"
                  aria-label="Draft playback position"
                  @input="onDraftScrub"
                  @change="onDraftScrubCommit"
                />
                <span class="voice-scrub-time">{{ formatClock(draftDuration) }}</span>
              </div>
              <button type="button" class="prompt-play" @click="toggleDraft">
                <i
                  :class="
                    draftPlaying && !draftPaused ? 'fas fa-pause' : 'fas fa-play'
                  "
                ></i>
                {{ draftPlaying && !draftPaused ? 'Pause' : 'Play' }}
              </button>
              <label class="motd-field-label" for="prompt-purpose">Purpose</label>
              <input
                id="prompt-purpose"
                v-model="draftPurpose"
                maxlength="80"
                placeholder="Friday evenings"
              />
              <label class="motd-field-label" for="prompt-by">Recorded by</label>
              <input
                id="prompt-by"
                v-model="draftBy"
                maxlength="80"
                placeholder="Aaron"
              />
              <label class="motd-field-label" for="prompt-desc">Description</label>
              <input
                id="prompt-desc"
                v-model="draftDescription"
                maxlength="200"
                placeholder="weekend standard message"
              />
              <div class="prompt-actions">
                <button
                  type="button"
                  class="motd-btn motd-btn-primary"
                  :disabled="savingPrompt || !draftPurpose.trim()"
                  @click="savePrompt"
                >
                  {{ savingPrompt ? 'Saving...' : 'Add message' }}
                </button>
                <button
                  type="button"
                  class="motd-btn motd-btn-secondary"
                  :disabled="savingPrompt"
                  @click="discardDraft"
                >
                  Discard
                </button>
              </div>
            </div>

            <label class="motd-field-label" for="prompt-filter">Filter</label>
            <input
              id="prompt-filter"
              v-model="filterText"
              placeholder="Purpose, name, or description"
            />

            <div class="prompt-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th class="col-check"></th>
                    <th>Purpose</th>
                    <th>Recorded by</th>
                    <th>Description</th>
                    <th>Length</th>
                    <th class="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!prompts.length">
                    <td colspan="6" class="prompt-empty">
                      No pre-recorded messages yet.
                    </td>
                  </tr>
                  <tr v-else-if="!filteredPrompts.length">
                    <td colspan="6" class="prompt-empty">
                      No messages match that filter.
                    </td>
                  </tr>
                  <template
                    v-for="(row, index) in filteredPrompts"
                    :key="row.id"
                  >
                    <tr :class="{ alt: index % 2 === 1 }">
                      <td class="col-check">
                        <input
                          type="checkbox"
                          :key="row.id + '-' + (row.selected ? 1 : 0) + '-' + choiceTick"
                          :checked="row.selected"
                          :disabled="choosing"
                          :aria-label="'Use ' + (row.purpose || 'this message')"
                          @change="choosePrompt(row, $event.target.checked)"
                        />
                      </td>
                      <td>{{ row.purpose }}</td>
                      <td>{{ row.recordedBy }}</td>
                      <td>{{ row.description }}</td>
                      <td class="col-length">{{ row.length }}</td>
                      <td class="col-actions">
                        <button
                          type="button"
                          class="prompt-icon"
                          :title="playingId === row.id && !playPaused ? 'Pause' : 'Play'"
                          @click="toggleRow(row)"
                        >
                          <i
                            :class="
                              playingId === row.id && !playPaused
                                ? 'fas fa-pause'
                                : 'fas fa-play'
                            "
                          ></i>
                        </button>
                        <button
                          type="button"
                          class="prompt-icon"
                          title="Delete"
                          :disabled="deletingId === row.id"
                          @click="deletePrompt(row)"
                        >
                          <i
                            :class="
                              deletingId === row.id
                                ? 'fas fa-spinner fa-spin'
                                : 'fas fa-trash'
                            "
                          ></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="playingId === row.id" class="scrub-row">
                      <td colspan="6">
                        <div class="voice-scrub">
                          <span class="voice-scrub-time">{{
                            formatClock(playCurrent)
                          }}</span>
                          <input
                            class="voice-scrub-bar"
                            type="range"
                            min="0"
                            step="0.05"
                            :max="playDuration || 0"
                            :value="playCurrent"
                            aria-label="Playback position"
                            @input="onScrubInput"
                            @change="onScrubCommit"
                          />
                          <span class="voice-scrub-time">{{
                            formatClock(playDuration)
                          }}</span>
                        </div>
                        <p v-if="playError" class="motd-error">{{ playError }}</p>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <p v-if="promptError" class="motd-error">{{ promptError }}</p>
          </template>
          <audio ref="draftPlayer" preload="auto"></audio>
          <audio ref="player" preload="auto"></audio>
        </template>
      </div>

      <div class="motd-footer">
        <button
          type="button"
          class="motd-btn motd-btn-secondary"
          @click="resetDefault"
          :disabled="loading || saving"
        >
          Use default
        </button>
        <button
          type="button"
          class="motd-btn motd-btn-secondary"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          type="button"
          class="motd-btn motd-btn-primary"
          :disabled="saving || loading || !say.trim()"
          @click="save"
        >
          {{ saving ? 'Saving...' : 'Save text' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import { filterVoicemailPrompts } from '@/utils/voicemailPrompts';
import {
  formatVoicePlaybackClock,
  clampVoiceSeekTime,
  sniffAudioMimeFromBytes,
} from '@/utils/voiceMessages';

const MAX_RECORD_SEC = 180;
const MAX_UPLOAD_BYTES = 12 * 1024 * 1024;

export default {
  name: 'VoicemailGreetingDialog',
  emits: ['close'],
  data() {
    return {
      closed: false,
      choiceTick: 0,
      loading: true,
      saving: false,
      say: '',
      defaultSay: '',
      errorText: '',
      successText: '',
      tableReady: true,
      recorderName: '',
      prompts: [],
      filterText: '',
      promptError: '',
      choosing: false,
      savingPrompt: false,
      deletingId: null,
      recording: false,
      recordElapsed: 0,
      recordStarted: 0,
      recordTimer: null,
      micStream: null,
      recorder: null,
      draftBlob: null,
      draftUrl: '',
      draftPurpose: '',
      draftBy: '',
      draftDescription: '',
      draftDuration: 0,
      draftCurrent: 0,
      draftPlaying: false,
      draftPaused: false,
      draftSeeking: false,
      playingId: null,
      playPaused: false,
      playCurrent: 0,
      playDuration: 0,
      playSeeking: false,
      playError: '',
      playUrl: '',
      audioEl: null,
      draftEl: null,
    };
  },
  computed: {
    filteredPrompts() {
      return filterVoicemailPrompts(this.prompts, this.filterText);
    },
    selectedId() {
      const hit = this.prompts.find((row) => row.selected);
      return hit ? hit.id : null;
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.closed = true;
    this.stopRecording();
    this.releaseMic();
    this.stopPlayback();
    this.discardDraft();
    if (this.recordTimer) clearInterval(this.recordTimer);
  },
  methods: {
    formatClock(seconds) {
      return formatVoicePlaybackClock(seconds);
    },
    async load() {
      this.loading = true;
      this.errorText = '';
      try {
        const [greeting, library] = await Promise.all([
          axios.get('/api/voicemail-greeting'),
          axios.get('/api/voicemail-prompts'),
        ]);
        this.defaultSay = greeting.data.defaultSay || '';
        this.say = greeting.data.say || this.defaultSay;
        this.applyLibrary(library.data);
      } catch (err) {
        this.errorText =
          err.response?.data?.error || 'Failed to load greeting.';
      } finally {
        this.loading = false;
        this.$nextTick(() => this.bindPlayers());
      }
    },
    applyLibrary(data) {
      const body = data || {};
      this.tableReady = body.tableReady !== false;
      this.recorderName = body.recorderName || '';
      this.prompts = Array.isArray(body.prompts) ? body.prompts : [];
      if (!this.draftBy) this.draftBy = this.recorderName;
    },
    resetDefault() {
      this.say = this.defaultSay;
      this.successText = '';
      this.errorText = '';
    },
    async save() {
      this.errorText = '';
      this.successText = '';
      const text = this.say.trim();
      if (!text) {
        this.errorText = 'Enter greeting text.';
        return;
      }
      this.saving = true;
      try {
        const { data } = await axios.put('/api/voicemail-greeting', {
          say: text,
        });
        this.say = data.say || text;
        this.successText = this.selectedId
          ? 'Text saved. Callers still hear the checked recording.'
          : 'Saved. The next caller will hear this greeting.';
      } catch (err) {
        this.errorText =
          err.response?.data?.error || 'Failed to save greeting.';
      } finally {
        this.saving = false;
      }
    },
    bindPlayers() {
      const draft = this.$refs.draftPlayer;
      const audio = this.$refs.player;
      if (draft && !this.onDraftTime) {
        this.draftEl = draft;
        this.onDraftTime = () => {
          if (this.draftSeeking) return;
          this.draftCurrent = draft.currentTime || 0;
          const dur = draft.duration;
          if (Number.isFinite(dur) && dur > 0) this.draftDuration = dur;
        };
        this.onDraftEnded = () => {
          this.draftPlaying = false;
          this.draftPaused = false;
          this.draftCurrent = 0;
        };
        draft.addEventListener('timeupdate', this.onDraftTime);
        draft.addEventListener('ended', this.onDraftEnded);
      }
      if (audio && !this.onAudioTime) {
        this.audioEl = audio;
        this.onAudioTime = () => {
          if (this.playSeeking) return;
          this.playCurrent = audio.currentTime || 0;
          const dur = audio.duration;
          if (Number.isFinite(dur) && dur > 0) this.playDuration = dur;
        };
        this.onAudioEnded = () => {
          this.playingId = null;
          this.playPaused = false;
          this.playCurrent = 0;
        };
        audio.addEventListener('timeupdate', this.onAudioTime);
        audio.addEventListener('ended', this.onAudioEnded);
      }
    },
    pickUpload() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    async onUpload(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      if (file.size > MAX_UPLOAD_BYTES) {
        this.promptError = 'That file is too large.';
        return;
      }
      this.promptError = '';
      this.stopPlayback();
      await this.setDraft(file, 0);
    },
    async startRecording() {
      this.promptError = '';
      this.stopPlayback();
      this.discardDraft();
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.promptError = 'This browser cannot record audio.';
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.micStream = stream;
        const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
        const mime = types.find(
          (type) => window.MediaRecorder && MediaRecorder.isTypeSupported(type),
        );
        const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        const chunks = [];
        rec.ondataavailable = (event) => {
          if (event.data && event.data.size) chunks.push(event.data);
        };
        rec.onstop = () => {
          const elapsed = this.recordElapsed;
          this.releaseMic();
          if (this.closed) return;
          const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
          this.setDraft(blob, elapsed);
        };
        this.recorder = rec;
        this.recording = true;
        this.recordStarted = Date.now();
        this.recordElapsed = 0;
        this.recordTimer = setInterval(() => {
          this.recordElapsed = (Date.now() - this.recordStarted) / 1000;
          if (this.recordElapsed >= MAX_RECORD_SEC) this.stopRecording();
        }, 200);
        rec.start();
      } catch {
        this.releaseMic();
        this.recording = false;
        this.promptError = 'Allow the microphone to record a greeting.';
      }
    },
    stopRecording() {
      if (this.recordTimer) {
        clearInterval(this.recordTimer);
        this.recordTimer = null;
      }
      this.recording = false;
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.stop();
      }
      this.recorder = null;
    },
    releaseMic() {
      if (!this.micStream) return;
      this.micStream.getTracks().forEach((track) => track.stop());
      this.micStream = null;
    },
    async setDraft(blob, fallbackDuration) {
      this.revokeDraft();
      this.draftBlob = blob;
      this.draftUrl = URL.createObjectURL(blob);
      this.draftDuration = Number(fallbackDuration) || 0;
      this.draftCurrent = 0;
      this.draftPlaying = false;
      this.draftPaused = false;
      await this.$nextTick();
      this.bindPlayers();
      const audio = this.$refs.draftPlayer;
      if (!audio) return;
      audio.src = this.draftUrl;
      audio.load();
    },
    revokeDraft() {
      if (this.draftEl) {
        this.draftEl.pause();
        this.draftEl.removeAttribute('src');
      }
      if (this.draftUrl) URL.revokeObjectURL(this.draftUrl);
      this.draftUrl = '';
      this.draftBlob = null;
      this.draftPlaying = false;
      this.draftPaused = false;
      this.draftCurrent = 0;
      this.draftDuration = 0;
    },
    discardDraft() {
      this.revokeDraft();
      this.draftPurpose = '';
      this.draftDescription = '';
    },
    async toggleDraft() {
      const audio = this.$refs.draftPlayer;
      if (!audio || !this.draftUrl) return;
      this.stopPlayback();
      if (this.draftPlaying && !audio.paused) {
        audio.pause();
        this.draftPaused = true;
        return;
      }
      this.draftPlaying = true;
      this.draftPaused = false;
      try {
        await audio.play();
      } catch (err) {
        this.promptError = 'Could not play that recording.';
        this.draftPlaying = false;
      }
    },
    onDraftScrub(event) {
      this.draftSeeking = true;
      const t = clampVoiceSeekTime(event.target.value, this.draftDuration);
      this.draftCurrent = t;
      if (this.draftEl) this.draftEl.currentTime = t;
    },
    onDraftScrubCommit(event) {
      const t = clampVoiceSeekTime(event.target.value, this.draftDuration);
      this.draftCurrent = t;
      if (this.draftEl) this.draftEl.currentTime = t;
      this.draftSeeking = false;
    },
    async savePrompt() {
      this.promptError = '';
      if (!this.draftBlob) return;
      if (!this.draftPurpose.trim()) {
        this.promptError = 'Enter a purpose.';
        return;
      }
      const form = new FormData();
      const name = this.draftBlob.name || 'greeting.webm';
      form.append('audio', this.draftBlob, name);
      form.append('purpose', this.draftPurpose.trim());
      form.append('recordedBy', this.draftBy.trim());
      form.append('description', this.draftDescription.trim());
      this.savingPrompt = true;
      try {
        await axios.post('/api/voicemail-prompts', form, { timeout: 120000 });
        const { data } = await axios.get('/api/voicemail-prompts');
        this.applyLibrary(data);
        this.discardDraft();
        this.successText = '';
      } catch (err) {
        this.promptError =
          err.response?.data?.error || 'Failed to save that recording.';
      } finally {
        this.savingPrompt = false;
      }
    },
    async choosePrompt(row, checked) {
      this.promptError = '';
      this.choosing = true;
      try {
        await axios.put('/api/voicemail-prompts/selected', {
          id: checked ? row.id : null,
        });
        this.prompts = this.prompts.map((item) => ({
          ...item,
          selected: checked && item.id === row.id,
        }));
        this.successText = '';
      } catch (err) {
        this.promptError =
          err.response?.data?.error || 'Failed to choose that message.';
      } finally {
        this.choiceTick += 1;
        this.choosing = false;
      }
    },
    async deletePrompt(row) {
      const warning = row.selected
        ? 'Delete this message? Callers will hear the text greeting until another is checked.'
        : 'Delete this message?';
      if (!window.confirm(warning)) return;
      this.deletingId = row.id;
      this.promptError = '';
      try {
        await axios.delete(`/api/voicemail-prompts/${row.id}`);
        if (this.playingId === row.id) this.stopPlayback();
        this.prompts = this.prompts.filter((item) => item.id !== row.id);
      } catch (err) {
        this.promptError =
          err.response?.data?.error || 'Failed to delete that message.';
      } finally {
        this.deletingId = null;
      }
    },
    stopPlayback() {
      if (this.audioEl) {
        this.audioEl.pause();
        this.audioEl.removeAttribute('src');
      }
      if (this.playUrl) URL.revokeObjectURL(this.playUrl);
      this.playUrl = '';
      this.playingId = null;
      this.playPaused = false;
      this.playCurrent = 0;
      this.playDuration = 0;
      this.playSeeking = false;
    },
    async toggleRow(row) {
      this.playError = '';
      this.bindPlayers();
      const audio = this.$refs.player;
      if (!audio) return;
      if (this.draftEl && this.draftPlaying) {
        this.draftEl.pause();
        this.draftPaused = true;
      }
      if (this.playingId === row.id) {
        if (audio.paused) {
          await audio.play();
          this.playPaused = false;
        } else {
          audio.pause();
          this.playPaused = true;
        }
        return;
      }
      this.stopPlayback();
      try {
        const response = await axios.get(`/api/voicemail-prompts/${row.id}/audio`, {
          responseType: 'blob',
          timeout: 60000,
        });
        const raw = response.data instanceof Blob ? response.data : new Blob([response.data]);
        const head = new Uint8Array(await raw.slice(0, 16).arrayBuffer());
        const mime = sniffAudioMimeFromBytes(head);
        const blob = raw.slice(0, raw.size, mime);
        this.playUrl = URL.createObjectURL(blob);
        audio.src = this.playUrl;
        audio.load();
        this.playingId = row.id;
        this.playPaused = false;
        this.playCurrent = 0;
        this.playDuration = 0;
        await audio.play();
      } catch (err) {
        this.playError = 'Could not play this recording.';
        this.stopPlayback();
      }
    },
    onScrubInput(event) {
      this.playSeeking = true;
      const t = clampVoiceSeekTime(event.target.value, this.playDuration);
      this.playCurrent = t;
      if (this.audioEl) this.audioEl.currentTime = t;
    },
    onScrubCommit(event) {
      const t = clampVoiceSeekTime(event.target.value, this.playDuration);
      this.playCurrent = t;
      if (this.audioEl) this.audioEl.currentTime = t;
      this.playSeeking = false;
    },
  },
};
</script>

<style scoped>
.motd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
}

.motd-dialog {
  width: min(880px, 96vw);
  max-height: 92vh;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
}

.motd-header {
  background: #a080a8;
  color: #fff;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.motd-header h3 {
  margin: 0;
  font-size: 1.05rem;
}

.motd-close {
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  font-size: 1.1rem;
}

.motd-body {
  padding: 16px;
  overflow: auto;
}

.motd-hint {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
}

.motd-loading {
  text-align: center;
  padding: 24px 0;
  color: #666;
}

.motd-field-label {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  margin: 10px 0 6px;
}

textarea,
input[type='text'],
input:not([type]) {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #bbb;
  border-radius: 4px;
  padding: 8px;
  font-size: 0.95rem;
}

textarea {
  resize: vertical;
  min-height: 88px;
}

.motd-error {
  color: #b00020;
  font-size: 0.85rem;
  margin: 10px 0 0;
}

.motd-success {
  color: #0f5132;
  font-size: 0.85rem;
  margin: 10px 0 0;
}

.prompt-heading {
  margin: 18px 0 8px;
  font-size: 1rem;
}

.prompt-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 12px;
}

.prompt-file {
  display: none;
}

.prompt-draft {
  border: 1px solid #e0d6e4;
  border-radius: 6px;
  padding: 10px 12px 12px;
  margin-bottom: 12px;
  background: #faf8fb;
}

.prompt-play {
  border: 1px solid #c8b7ce;
  background: #fff;
  color: #4a3a52;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
}

.prompt-table-wrap {
  overflow-x: auto;
  border: 1px solid #e0d6e4;
  border-radius: 6px;
  margin-top: 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  min-width: 640px;
}

th,
td {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

th {
  background: #f6f1f8;
  font-weight: 600;
}

tr.alt td {
  background: #faf8fb;
}

.col-check {
  width: 36px;
}

.col-check input {
  width: 18px;
  height: 18px;
}

.col-length {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.col-actions {
  width: 84px;
  white-space: nowrap;
  text-align: right;
}

.prompt-empty {
  color: #666;
  text-align: center;
}

.prompt-icon {
  border: 1px solid #c8b7ce;
  background: #fff;
  color: #4a3a52;
  border-radius: 6px;
  width: 34px;
  height: 34px;
  margin-left: 4px;
  cursor: pointer;
}

.prompt-icon:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.scrub-row td {
  background: #fff;
  padding-top: 0;
}

.voice-scrub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 8px;
}

.voice-scrub-time {
  flex-shrink: 0;
  font-size: 0.72em;
  color: #6a5570;
  font-variant-numeric: tabular-nums;
  min-width: 2.2em;
}

.voice-scrub-bar {
  flex: 1;
  min-width: 0;
  height: 18px;
  margin: 0;
  accent-color: #a080a8;
  cursor: pointer;
}

.motd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #ddd;
  background: #efefef;
}

.motd-btn {
  border: none;
  border-radius: 4px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 0.9rem;
  min-height: 40px;
}

.motd-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.motd-btn-secondary {
  background: #ddd;
  color: #333;
}

.motd-btn-primary {
  background: #5f4b8b;
  color: #fff;
}
</style>
